import {
  OrderStatus,
  buildExpenseReviewKey,
  getReportSelectionBlockReasonKey,
  type OrderExpenseLine,
  type OrderExpenseReviewState,
  type OrderHistory,
  type OrderRevisionNote,
} from "./order";
import {
  areAllEmbeddedExpensesReviewChecked,
  getExpenseReview,
} from "../utils/embeddedExpenseReview";
import { normalizeAmount } from "../utils/formatRupiah";

export interface StandbyExpenseLine {
  id?: string;
  type: "standby" | "additional";
  amount: number;
  notes?: string;
  overrideAmount?: number;
  review?: OrderExpenseReviewState;
}

export interface StandbyOrderFormAdditionalExpense {
  amount: string;
  notes: string;
}

export interface StandbyOrder {
  _id: string;
  readableId: string;
  companyId: string;
  teamOperationsId: string;
  createdByUserId: string;
  status: OrderStatus;
  uangJalanReportId?: string;
  uangJalanReportReadableId?: string;
  date?: string;
  truckId?: string;
  driverId?: string;
  locationId?: string;
  expenses: StandbyExpenseLine[];
  note?: string;
  overrideNotes?: string;
  revisionNote?: OrderRevisionNote;
  totalPaid: number;
  totalApprovedCost: number;
  difference: number;
  teamOperations?: { _id: string; name: string };
  truck?: { _id: string; licensePlate: string };
  driver?: { _id: string; name: string; profilePictureUrl?: string | null };
  location?: { _id: string; name: string };
  creator?: { _id: string; name: string };
  histories?: OrderHistory[];
}

export interface StandbyOrderSearchFilters {
  teamOperationsId?: string;
  status?: string;
  statuses?: string[];
}

export interface StandbyOrderFormValues {
  teamOperationsId: string;
  date: string;
  truckId: string;
  driverId: string;
  locationId: string;
  note: string;
  expenseStandby: string;
  expenseAdditional: StandbyOrderFormAdditionalExpense[];
}

export const emptyStandbyOrderFormValues = (): StandbyOrderFormValues => ({
  teamOperationsId: "",
  date: "",
  truckId: "",
  driverId: "",
  locationId: "",
  note: "",
  expenseStandby: "",
  expenseAdditional: [],
});

export const getStandbyAdditionalExpenses = (
  expenses: StandbyExpenseLine[] | undefined,
): StandbyExpenseLine[] => {
  return (expenses ?? []).filter((line) => line.type === "additional");
};

export const getStandbyExpenseLine = (
  expenses: StandbyExpenseLine[] | undefined,
): StandbyExpenseLine | undefined => {
  return (expenses ?? []).find((line) => line.type === "standby");
};

export const standbyOrderToFormValues = (order: StandbyOrder): StandbyOrderFormValues => ({
  teamOperationsId: order.teamOperationsId,
  date: order.date ?? "",
  truckId: order.truckId ?? "",
  driverId: order.driverId ?? "",
  locationId: order.locationId ?? "",
  note: order.note ?? "",
  expenseStandby: String(getStandbyExpenseLine(order.expenses)?.amount ?? ""),
  expenseAdditional: getStandbyAdditionalExpenses(order.expenses).map((item) => ({
    amount: String(item.amount ?? ""),
    notes: item.notes ?? "",
  })),
});

export const mapStandbyOrderResponse = (response: any): StandbyOrder => ({
  _id: response._id,
  readableId: response.readableId,
  companyId: response.companyId,
  teamOperationsId: response.teamOperationsId,
  createdByUserId: response.createdByUserId,
  status: response.status,
  uangJalanReportId: response.uangJalanReportId,
  uangJalanReportReadableId: response.uangJalanReportReadableId,
  date: response.date,
  truckId: response.truckId,
  driverId: response.driverId,
  locationId: response.locationId,
  expenses: (response.expenses ?? []).map((line: any) => ({
    id: line.id ?? line._id,
    type: line.type === "additional" ? ("additional" as const) : ("standby" as const),
    amount: line.amount ?? 0,
    notes: line.notes,
    overrideAmount: line.overrideAmount,
    review: line.review,
  })),
  note: response.note,
  overrideNotes: response.overrideNotes,
  revisionNote: response.revisionNote,
  totalPaid: response.totalPaid ?? 0,
  totalApprovedCost: response.totalApprovedCost ?? 0,
  difference: response.difference ?? 0,
  teamOperations: response.teamOperations,
  truck: response.truck,
  driver: response.driver,
  location: response.location,
  creator: response.creator,
  histories: response.histories,
});

export const mapStandbyOrdersResponse = (response: any): StandbyOrder[] => {
  const items = response?.data?.standbyOrders ?? response?.standbyOrders ?? [];
  return items.map(mapStandbyOrderResponse);
};

export const canSelectStandbyForReport = (
  order: StandbyOrder,
  selectedTeamOperationsId?: string,
): boolean => {
  return getReportSelectionBlockReasonKey(order, selectedTeamOperationsId) === null;
};

const parseFormNumber = (value: string): number => {
  const parsed = Number(value);
  return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

const buildStandbyExpensesFromFormValues = (
  values: StandbyOrderFormValues,
  additionalExpenseIds?: string[],
): Array<{ type: "standby" | "additional"; amount: number; notes?: string; id?: string }> => {
  const expenses: Array<{
    type: "standby" | "additional";
    amount: number;
    notes?: string;
    id?: string;
  }> = [{ type: "standby", amount: parseFormNumber(values.expenseStandby) }];

  const additionalRows = values.expenseAdditional.filter(
    (entry) => entry.amount.trim() !== "" || entry.notes.trim() !== "",
  );
  additionalRows.forEach((row, index) => {
    const line: {
      type: "additional";
      amount: number;
      notes: string;
      id?: string;
    } = {
      type: "additional",
      amount: Number(row.amount),
      notes: row.notes.trim(),
    };
    const id = additionalExpenseIds?.[index];
    if (id) {
      line.id = id;
    }
    expenses.push(line);
  });

  return expenses;
};

export const buildStandbyCreatePayload = (values: StandbyOrderFormValues) => ({
  teamOperationsId: values.teamOperationsId,
  date: values.date || undefined,
  truckId: values.truckId || undefined,
  driverId: values.driverId || undefined,
  locationId: values.locationId || undefined,
  ...(values.note?.trim() ? { note: values.note.trim() } : {}),
  expenses: buildStandbyExpensesFromFormValues(values),
});

export const buildStandbyUpdatePayload = (
  values: StandbyOrderFormValues,
  options?: { additionalExpenseIds?: string[] },
) => ({
  date: values.date || null,
  truckId: values.truckId || null,
  driverId: values.driverId || null,
  locationId: values.locationId || null,
  note: (values.note ?? "").trim() || null,
  expenses: buildStandbyExpensesFromFormValues(values, options?.additionalExpenseIds),
});

export const validateStandbyOrderForm = (
  values: StandbyOrderFormValues,
  options?: { requireTeam?: boolean },
): string[] => {
  const errors: string[] = [];
  if (options?.requireTeam !== false && !values.teamOperationsId) {
    errors.push("standby.validation.teamRequired");
  }
  const amount = Number(values.expenseStandby);
  if (!values.expenseStandby || Number.isNaN(amount) || amount <= 0) {
    errors.push("standby.validation.amountRequired");
  }
  values.expenseAdditional.forEach((row, index) => {
    const hasAmount = row.amount.trim() !== "";
    const hasNotes = row.notes.trim() !== "";
    if (!hasAmount && !hasNotes) {
      return;
    }
    if (!hasNotes) {
      errors.push(`orders.validation.additionalNotesRequired:${index}`);
    }
    if (!hasAmount || Number(row.amount) <= 0) {
      errors.push(`orders.validation.additionalAmountRequired:${index}`);
    }
  });
  return errors;
};

export const getStandbyReadyForReviewMissingFieldKeys = (order: StandbyOrder): string[] => {
  const missing: string[] = [];
  if (!order.date) {
    missing.push("standby.validation.dateRequired");
  }
  if (!order.driverId) {
    missing.push("standby.validation.driverRequired");
  }
  if (!order.truckId) {
    missing.push("standby.validation.truckRequired");
  }
  if (!order.locationId) {
    missing.push("standby.validation.locationRequired");
  }
  const amount = getStandbyExpenseLine(order.expenses)?.amount ?? 0;
  if (!(amount > 0)) {
    missing.push("standby.validation.amountRequired");
  }
  getStandbyAdditionalExpenses(order.expenses).forEach((item, index) => {
    if (!item.notes?.trim()) {
      missing.push(`orders.validation.additionalNotesRequired:${index}`);
    }
    if (!item.amount || item.amount <= 0) {
      missing.push(`orders.validation.additionalAmountRequired:${index}`);
    }
  });
  return missing;
};

export const isStandbyReadyForReview = (order: StandbyOrder): boolean => {
  return (
    order.status === OrderStatus.OPEN &&
    getStandbyReadyForReviewMissingFieldKeys(order).length === 0
  );
};

export const areAllStandbyExpenseReviewItemsChecked = (order: StandbyOrder): boolean => {
  return areAllEmbeddedExpensesReviewChecked(order.expenses as unknown as OrderExpenseLine[]);
};

export const buildStandbyExpenseChecklistItems = (
  order: StandbyOrder,
  standbyLabel: string,
): Array<{
  key: string;
  label: string;
  amount: number;
  checked: boolean;
  needsRevision: boolean;
  overrideAmount?: number;
  isAdditional?: boolean;
}> => {
  return (order.expenses ?? []).flatMap((line) => {
    if (line.type === "additional" && !line.id) {
      return [];
    }
    const review = getExpenseReview(line as OrderExpenseLine);
    const item: {
      key: string;
      label: string;
      amount: number;
      checked: boolean;
      needsRevision: boolean;
      overrideAmount?: number;
      isAdditional?: boolean;
    } = {
      key: buildExpenseReviewKey(line as OrderExpenseLine),
      label: line.type === "additional" ? line.notes || "" : standbyLabel,
      amount: normalizeAmount(line.amount),
      checked: review.checked,
      needsRevision: review.needsRevision,
    };
    if (line.type === "additional") {
      item.isAdditional = true;
    }
    if (line.overrideAmount !== undefined && line.overrideAmount !== null) {
      item.overrideAmount = normalizeAmount(line.overrideAmount);
    }
    return [item];
  });
};
